import { useEffect, useRef } from "react";
import { usePage } from "@inertiajs/react";
import { useCsrfToken } from "@/utils/csrf";
import { clearFavorites, getOrderedFavoriteIds } from "@/utils/favoriteStorage";
import type { SharedProps } from "@/types";

// ログイン直後に LocalStorage のお気に入りを /favorites/sync へ送って DB へマージする。
// 成功時のみ LocalStorage をクリアし、失敗時は保持して次回ログインセッションで再試行する。
// 共通レイアウト/エントリーポイントで一度だけ起動する想定。
export function useFavoriteSync(): void {
  const { auth } = usePage<SharedProps>().props;
  const csrfToken = useCsrfToken();
  // 同一マウント中に重複起動しないためのガード。
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (auth.user === null || attemptedRef.current) return;

    const { reportIds, photoIds } = getOrderedFavoriteIds();
    if (reportIds.length === 0 && photoIds.length === 0) return;

    attemptedRef.current = true;

    fetch("/favorites/sync", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      body: JSON.stringify({ report_event_ids: reportIds, photo_ids: photoIds }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`sync failed: ${response.status}`);
        // 成功時のみクリア。失敗時は保持して次回再試行する。
        clearFavorites("marry_report_events");
        clearFavorites("marry_report_photos");
      })
      .catch(() => {
        // 同期失敗は致命的ではないため握り潰し、LocalStorage を残して次回に委ねる。
        attemptedRef.current = false;
      });
  }, [auth.user, csrfToken]);
}
